import { useState, useEffect, useMemo, useRef, useCallback, type CSSProperties } from "react"
import { useLocation } from "react-router-dom"

import { useShallow } from "zustand/react/shallow"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import type { Conversation, NavTab, Broadcast, Message } from "./types"
import { selectUiConversations, useMessengerStore } from "./store/messenger.store"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { userDisplayName } from "@/features/auth/utils/display-name"
import { subscribeWebPushOpen } from "@/sdk/instavcMessenger-instance"
import { ContactTermsGate } from "./contact-terms-gate"
import { useContactRegistration } from "./hooks/useContactRegistration"
import { useConversationActions } from "./hooks/useConversationActions"
import { useForwardMessage } from "./hooks/useForwardMessage"
import { useBlockedContacts } from "./hooks/useBlockedContacts"
import { useChatPrivacy } from "./hooks/useChatPrivacy"
import { useStarredMessages } from "./hooks/useStarredMessages"
import { useChatPreferences } from "./hooks/useChatPreferences"
import { setConversationBackground } from "@/sdk/instavcMessenger-instance"
import { messengerApi, MessengerApiError } from "./services/messenger.api"
import { groupActionReason } from "./group-error"
import { inviteIdFromPath, decideInviteMembership } from "./invite-link"
import { JoinGroupDialog } from "./join-group-dialog"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { usePresenceSubscription } from "@/features/presence/hooks/usePresenceSubscription"
import { presenceApi } from "@/features/presence/services/presence.api"
import { usePresenceStore } from "@/features/presence/store/presence.store"
import { type SearchScope } from "@/features/meetings/services/contacts.api"
import { useDirectorySearch } from "./hooks/useDirectorySearch"
import { ChatNavBar } from "./chat-nav-bar"
import { ChatListPanel } from "./chat-list-panel"
import { ConversationPanel } from "./conversation-panel"
import { EmptyState } from "./empty-state"
import { CallsTab } from "./calls-tab"
import { BroadcastTab } from "./broadcast-tab"
import { useBroadcasts } from "./hooks/useBroadcasts"
import { GroupsTab } from "./groups-tab"
import { SettingsTab } from "./settings-tab"
import { BlockedContactsPage } from "./blocked-contacts-page"
import { ChatBackgroundPage } from "./chat-background-page"
import { ChatColorPage } from "./chat-color-page"
import { StarredMessagesPage } from "./starred-messages-page"
import { IncomingCallOverlay } from "./incoming-call-overlay"
import { CallScreen } from "./call-screen"
import { CreateGroupDialog } from "./create-group-dialog"
import { NewChatPage } from "./new-chat-page"
import { ProfileInfoPanel } from "./profile-info-panel"
import { useCallsStore } from "./store/calls.store"
import { Drawer, DrawerContent } from "@/clientWeb/components/ui/drawer"

type SettingsPage = "main" | "blocked-contacts" | "chat-background" | "chat-color" | "starred"

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Preview text for an attachment-only starred message (it has no body). */
const ATTACHMENT_PREVIEW: Record<string, string> = {
  photo: "📷 Photo",
  video: "🎥 Video",
  audio: "🎵 Audio",
  voice: "🎤 Voice message",
  document: "📄 Document",
}

/** A starred row's first attachment, in the UI message's attachment fields. */
function attachmentFields(
  a: { url: string | null; filename?: string | null; sizeBytes?: number; mime?: string; thumbnailUrl?: string | null } | undefined,
): Partial<Message> {
  if (!a) return {}
  return {
    attachmentUrl: a.url ?? undefined,
    attachmentName: a.filename ?? undefined,
    attachmentSize: a.sizeBytes,
    attachmentMime: a.mime,
    attachmentThumbnailUrl: a.thumbnailUrl ?? undefined,
  }
}

/** Attachment kind → the UI message type the Starred row renders as. */
const ATTACHMENT_UI_TYPE: Record<string, Message["type"]> = {
  photo: "image",
  video: "video",
  audio: "audio",
  voice: "audio",
  document: "file",
}

/**
 * Find a loaded wire message by id, checking [preferConversationId] first (the
 * open thread, where a forward almost always originates) before scanning the
 * rest. Returns undefined when the row was never loaded as wire.
 */
function findWireMessage(messageId: string, preferConversationId?: string) {
  const threads = useMessengerStore.getState().wireMessagesByConv
  const preferred = preferConversationId ? threads.get(preferConversationId) : undefined
  const hit = preferred?.find((m) => m.id === messageId)
  if (hit) return hit
  for (const [convId, thread] of threads) {
    if (convId === preferConversationId) continue
    const found = thread.find((m) => m.id === messageId)
    if (found) return found
  }
  return undefined
}

export default function PresencePage() {
  const location = useLocation()
  const [navTab, setNavTab] = useState<NavTab>("chats")
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [selectedBroadcast, setSelectedBroadcast] = useState<Broadcast | null>(null)
  const [settingsPage, setSettingsPage] = useState<SettingsPage>("main")
  const [incomingCall, setIncomingCall] = useState<{ name: string; image?: string; isVideo: boolean } | null>(null)
  const [activeGlobalCall, setActiveGlobalCall] = useState<{ name: string; image?: string; mode: "audio" | "video" } | null>(null)
  const [showNewChatDialog, setShowNewChatDialog] = useState(false)
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false)
  const [showCreateBroadcastDialog, setShowCreateBroadcastDialog] = useState(false)
  const [viewProfileConv, setViewProfileConv] = useState<Conversation | null>(null)
  // W13 swap — conversations now derive from wire state (real REST seed +
  // live channel deltas via `useConversationList`). `useShallow` keeps the
  // projected array referentially stable when the underlying wire data is
  // unchanged.
  // CHAT/PRESENCE identity = the per-org contactPresenceId (NOT the auth user id,
  // which stays for meetings/profile). Null until resolved/consented — every
  // chat/presence effect below is guarded on it, so nothing acts while null.
  // The first-use terms gate is mounted app-wide in DashboardLayout.
  const myUserId = useAuthStore((s) => s.presenceId)
  // The auth user — for the account avatar shown on the settings profile card.
  const me = useAuthStore((s) => s.user)
  // Chat is optional: if the user opens the chat tab without a contact yet, show
  // the DISMISSIBLE terms gate here (never app-wide). "Maybe later" just leaves
  // chat inactive; accepting mints the contactPresenceId.
  const contactGate = useContactRegistration()
  // NOTE: the conversation list + per-conversation channel subscriptions are
  // now mounted app-wide in DashboardLayout (so chat works on every page, not
  // just here). PresencePage just reads the resulting store state.
  const { forward: forwardMessage } = useForwardMessage()
  // Server-backed block list — drives the settings page, the conversation
  // banner, and the block/unblock actions. Enforcement itself is server-side.
  const blockList = useBlockedContacts()
  // Mounted here (not only in the settings tab) so read-receipt reciprocity is
  // in force wherever ticks render — otherwise a user with receipts off would
  // still see everyone's blue ticks until they happened to open Settings.
  useChatPrivacy()
  // Server-backed starred messages — private to this user, synced across their
  // devices. Owned here so the Starred page and every thread share one set.
  const stars = useStarredMessages()
  // Account-level preferences (theme + DEFAULT chat background), synced across
  // devices. A thread's own override lives on its participant row and arrives
  // on the conversation payload as `background`.
  const chatPrefs = useChatPreferences()
  // Message to scroll to after a jump from the Starred list.
  const [jumpToMessageId, setJumpToMessageId] = useState<string | null>(null)
  // CDT-6 — right-click menu actions (pin/mute/archive/delete/mark-read) now
  // round-trip through the REST API with optimistic local mutations.
  const convActions = useConversationActions()
  const t = useTranslation()
  const conversations = useMessengerStore(
    useShallow((s) => selectUiConversations(s, myUserId ?? null)),
  )
  // ─── Notification click → open that thread ────────────────────────────────
  // Two arrival paths, because the service worker runs outside the page:
  //   • no tab open  → the SW opens one with `?c=<id>` (read once, below)
  //   • tab already open → the SW postMessages the payload (subscribe, below)
  // focus() alone would leave the user on whatever page they were showing.
  const [pendingConversationId, setPendingConversationId] = useState<string | null>(() => {
    const c = new URLSearchParams(window.location.search).get("c")
    return c && UUID_RE.test(c) ? c : null
  })

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("tab") === "calls") setNavTab("calls")
  }, [])

  // The left rail's Chats item always wants Presence's own Chats tab, even when
  // the route is already /chats (e.g. coming from Presence's Settings tab, where
  // the path never changes so a plain navigate() is a no-op). It sends a fresh
  // `at` each click so this still fires on a repeat click from the same tab.
  // Mirrors ChatNavBar's own tab switch below: dropping back to "main" so a
  // settings sub-page left open doesn't cover the chat list.
  useEffect(() => {
    if (location.state?.presenceTab === "chats") {
      setNavTab("chats")
      setSettingsPage("main")
    }
  }, [location.state])

  /**
   * Leaving the Chats tab closes New Chat.
   *
   * The picker's visibility is a separate flag from the tab, so it survived a
   * trip to Calls or Broadcast: coming back to Chats reopened the contact
   * picker rather than the chat list, and the only way out was the Close
   * button. Opening it is a deliberate act that belongs to the Chats tab, so
   * it does not outlive a move away from it.
   *
   * Handled once here rather than at each `setNavTab` call — there are seven,
   * and a future eighth would otherwise reintroduce this.
   */
  useEffect(() => {
    if (navTab !== "chats") setShowNewChatDialog(false)
  }, [navTab])

  // ─── Invite link → confirm membership, then open that thread ──────────────
  //
  // `/chats/invite/<conversationId>` renders THIS page on a fresh load (pasted
  // in the address bar, opened from another site); the effect below reads the
  // id off the location once and hands it to `openInvite`. A link clicked
  // inside a chat message never routes at all: the conversation panel calls
  // `openInvite` directly, so the page keeps its state. `lastInviteId` keeps
  // an address-bar id from being handled twice.
  //
  // Membership is never guessed from the locally loaded conversation list,
  // which may not have arrived yet. `getConversation` succeeding on this id
  // IS membership (the server only serves that route to a current member), so
  // that round trip is the source of truth, not what happens to be on screen.
  const lastInviteId = useRef<string | null>(null)
  const [joinInvite, setJoinInvite] = useState<{ id: string; groupName: string | null } | null>(null)

  // Shared by both entry points: a link clicked inside a chat calls this
  // directly, and an invite address opened from outside reaches it through
  // the effect below.
  const openInvite = useCallback((id: string) => {
    messengerApi
      .getConversation(id)
      .then(({ conversation }) => {
        // Put the row in the list before selecting it: the selection waits
        // for the row to exist, and a group opened from a link is not always
        // in the list that has loaded so far.
        useMessengerStore.getState().upsertWireConversation(conversation)
        setPendingConversationId(id)
      })
      .catch((err) => {
        const decision = decideInviteMembership(
          err instanceof MessengerApiError ? { ok: false, status: err.status, code: err.code } : { ok: false },
        )
        if (decision === "confirm") {
          // A name for this group is not something joining is required to
          // learn, only something to use if it was already loaded for some
          // other reason. Usually it was not, and the dialog falls back to
          // its generic title.
          const knownName = conversations.find((c) => c.id === id)?.name ?? null
          setJoinInvite({ id, groupName: knownName })
          return
        }
        // Stay on the chat list rather than stranding them: the interface they
        // wanted is already on screen, so the toast is the only thing needed.
        toast.error({
          title: t("presence.inviteOpenFailed"),
          description: groupActionReason(err, t),
        })
      })
  }, [conversations, t])

  useEffect(() => {
    const id = inviteIdFromPath(location.pathname)
    if (!id || lastInviteId.current === id) return
    // presenceId arrives from a contact lookup shortly after the session, and
    // it is the id this conversation service keys members by, so wait for it
    // rather than treating "not resolved yet" as "not signed in".
    if (!myUserId) return
    lastInviteId.current = id
    // Back to /chats without a route change, so a refresh or Back does not
    // replay the invite. Routing to /chats instead could remount this page
    // and drop the selection that is about to be made.
    window.history.replaceState(window.history.state, "", "/chats")
    openInvite(id)
  }, [location.pathname, myUserId, openInvite])

  const handleJoinInviteConfirm = () => {
    if (!joinInvite || !myUserId) return
    const { id } = joinInvite
    setJoinInvite(null)
    messengerApi
      .addMembers(id, [myUserId])
      // Now a member: openInvite fetches the group, adds it to the list and
      // selects it, the same path as an invite for a group already joined.
      .then(() => openInvite(id))
      .catch((err) => {
        toast.error({
          title: t("presence.joinGroupFailed"),
          description: groupActionReason(err, t),
        })
      })
  }

  const handleJoinInviteCancel = () => setJoinInvite(null)

  useEffect(() => subscribeWebPushOpen((open) => {
    const id = open.conversationId
    if (open.type === "call.invite") { setNavTab("calls"); return }
    if (id && UUID_RE.test(id)) setPendingConversationId(id)
  }), [])

  // Conversations load asynchronously, so hold the target until its row exists
  // — a click routinely lands before the list has arrived.
  useEffect(() => {
    if (!pendingConversationId) return
    const match = conversations.find((c) => c.id === pendingConversationId)
    if (!match) return
    setNavTab("chats")
    // Leave New Chat if it is open. `renderLeftContent` tests
    // `showNewChatDialog` BEFORE anything else, so the contact picker keeps
    // covering the panel no matter which conversation is selected — clicking a
    // new-message notification set the conversation and the tab, then appeared
    // to do nothing because the picker was still on top of it.
    setShowNewChatDialog(false)
    setSelectedConversation(match)
    setPendingConversationId(null)
    // Strip ?c= so a later refresh doesn't reopen the thread.
    const url = new URL(window.location.href)
    if (url.searchParams.has("c")) {
      url.searchParams.delete("c")
      window.history.replaceState({}, "", `${url.pathname}${url.search}`)
    }
  }, [pendingConversationId, conversations])

  const setConversations = useMessengerStore((s) => s.setConversations)
  const toggleFavorite = useMessengerStore((s) => s.toggleFavorite)
  // Broadcast lists — real, through the SDK (`sdk.broadcasts`). Owner-private:
  // sending fans out into each recipient's DM server-side.
  const {
    broadcasts,
    isLoading: broadcastsLoading,
    error: broadcastsError,
    create: createBroadcastList,
    rename: renameBroadcastList,
    remove: removeBroadcastList,
    removeRecipient: removeBroadcastRecipientFromList,
    refresh: refreshBroadcasts,
  } = useBroadcasts()
  const messagesByConversation = useMessengerStore((s) => s.messagesByConversation)

  // Subscribe to live presence for everyone we have a conversation with
  // plus our org-mates from the contacts API (real iam.users UUIDs).
  // Non-UUID ids (mock conversation data) are filtered out so the REST seed
  // doesn't 400; when real conversations land they'll merge in naturally.
  // Contact list for the "New Chat" picker, loaded LAZILY (only when the picker
  // opens, `enabled` below), one page at a time via `useDirectorySearch`
  // (debounce, scope widening and `loadMore` all live there now; this page just
  // owns the scope/query the picker is on). We deliberately do NOT fetch
  // presence for contacts, only for conversation peers (see watchedUserIdsKey
  // below).
  const [pickerScope, setPickerScope] = useState<SearchScope>("global")
  const [pickerQuery, setPickerQuery] = useState("")
  const {
    contacts: chatPickerContacts,
    loading: pickerLoading,
    loadingMore: pickerLoadingMore,
    hasMore: pickerHasMore,
    loadMore: loadMorePickerContacts,
    resultScope: pickerResultScope,
  } = useDirectorySearch({ enabled: !!myUserId && showNewChatDialog, scope: pickerScope, query: pickerQuery })

  // Reset scope/query each time the picker opens so it always starts on
  // Global, the whole platform, which is what "New Chat" should show first.
  useEffect(() => {
    if (showNewChatDialog) { setPickerScope("global"); setPickerQuery("") }
  }, [showNewChatDialog])

  // The "New Group" / "New Broadcast" participant picker now owns its own
  // directory fetch (scope tabs + search + paging), the same as New Chat:
  // see CreateGroupDialog. Nothing to load here.

  // Watch presence ONLY for conversation peers (the people shown in the chat
  // list). Contacts you've never chatted with don't need a presence dot on the
  // chat screen. Stable comma-string key so the seed doesn't re-fire per render.
  const watchedUserIdsKey = useMemo(() => {
    const ids = new Set<string>()
    for (const c of conversations) {
      for (const p of c.participants ?? []) {
        if (p.id && p.id !== myUserId && UUID_RE.test(p.id)) ids.add(p.id)
      }
    }
    return Array.from(ids).sort().join(",")
  }, [conversations, myUserId])

  const watchedUserIds = useMemo(
    () => (watchedUserIdsKey ? watchedUserIdsKey.split(",") : []),
    [watchedUserIdsKey],
  )

  usePresenceSubscription(watchedUserIds)

  // REST seed so dots paint immediately (before the socket round-trip). The
  // server derives my conversation peers from the token — we don't send a
  // userIds list. Runs once per login; new conversations get their presence
  // live via the socket subscription above.
  useEffect(() => {
    if (!myUserId) return
    let cancelled = false
    presenceApi.listMine().then(({ presence }) => {
      if (!cancelled) usePresenceStore.getState().setMany(presence)
    }).catch(() => { /* network blip — socket will fill in */ })
    return () => { cancelled = true }
  }, [myUserId])

  const [messagesStore, setMessagesStore] = useState<Record<string, Message[]>>({})
  useEffect(() => {
    setMessagesStore(messagesByConversation)
  }, [messagesByConversation])
  // The DEFAULT background. Was local `useState("")` — not even persisted, so
  // it reset on every reload. Now an account preference.
  const chatBackground = chatPrefs.preferences.chatBackground ?? ""
  // Accent colour for chat bubbles/avatars. Was local `useState("")`, so the
  // Save button applied it for the session and it was gone on the next reload.
  // Now an account preference — it persists and follows the user's devices.
  const chatColor = chatPrefs.preferences.chatColor ?? ""
  // Signed-in user by default, overridden only when they edit it here. Derived
  // rather than seeded into state so it still fills in if auth hydrates late.
  const authUser = useAuthStore((s) => s.user)
  const [editedProfileName, setProfileName] = useState<string | null>(null)
  const profileName = editedProfileName ?? userDisplayName(authUser)
  // The signed-in user's own avatar — this was a hardcoded stock photo of a
  // stranger, so every account showed the same face. `setProfileImage` still
  // exists for the in-session profile edit; the initial value now comes from
  // the account.
  const [profileImage, setProfileImage] = useState(me?.avatarUrl ?? "")

  // Adopt the account avatar once auth hydrates (it's null on first render).
  useEffect(() => {
    if (me?.avatarUrl) setProfileImage(me.avatarUrl)
  }, [me?.avatarUrl])

  // Incoming call simulation — commented out for now
  // useEffect(() => {
  //   const t = setTimeout(() => {
  //     setIncomingCall({ name: "Alex Thompson", image: "https://randomuser.me/api/portraits/men/32.jpg", isVideo: false })
  //   }, 5000)
  //   return () => clearTimeout(t)
  // }, [])

  const handleSelectConversation = (c: Conversation) => {
    // Opening a conversation always leaves the contact picker, wherever the
    // call came from. Without this, any caller that is not the chat list —
    // the Groups tab, a notification, anything added later — selects a
    // conversation the reader cannot see.
    setShowNewChatDialog(false)
    setSelectedConversation(c)
    setConversations(prev => prev.map(conv => conv.id === c.id ? { ...conv, unreadCount: 0 } : conv))
  }
  const handleCloseConversation = () => setSelectedConversation(null)

  const handleArchiveConv = (id: string) => {
    // Backend has no "toggle" — read current state, flip.
    const conv = conversations.find(c => c.id === id)
    const next = !conv?.isArchived
    setConversations(prev => prev.map(c => c.id === id ? { ...c, isArchived: next } : c))
    if (next && selectedConversation?.id === id) setSelectedConversation(null)
    void (next ? convActions.archive(id) : convActions.unarchive(id))
  }
  const handlePinConv = (id: string) => {
    setConversations(prev => prev.map(c => c.id === id ? { ...c, isPinned: !c.isPinned } : c))
    void convActions.togglePin(id)
  }
  const handleMuteConv = (id: string) => {
    setConversations(prev => prev.map(c => c.id === id ? { ...c, isMuted: !c.isMuted } : c))
    void convActions.toggleMute(id)
  }
  const handleDeleteConv = (id: string, opts: { keepStarred: boolean } = { keepStarred: true }) => {
    setConversations(prev => prev.filter(c => c.id !== id))
    if (selectedConversation?.id === id) setSelectedConversation(null)
    void convActions.deleteChat(id, opts)
  }
  /**
   * Exit Group: the real leave, dropping my participant row instead of just
   * hiding the thread. It waits for the server — a "left" message posts to the
   * other members — and then the group STAYS in my list, read-only, with its
   * history: Delete group (handleDeleteConv) is what removes it.
   */
  const handleExitGroup = async (id: string) => {
    const name = conversations.find(c => c.id === id)?.name ?? ''
    try {
      await convActions.exitGroup(id)
      // The group stays in the list, read-only; Delete group removes it.
      toast.success({ title: t('presence.leftGroup'), description: `You have left ${name}.` })
    } catch (err) {
      toast.error({
        title: t('presence.exitGroupFailed'),
        description: err instanceof Error ? err.message : undefined,
      })
    }
  }
  const handleFavoriteConv = (id: string) => {
    // Client-only concept (no server column) — persisted in the store's
    // favoriteIds set (localStorage) and projected onto Conversation.isFavorite.
    toggleFavorite(id)
  }
  const handleMarkAsReadConv = (id: string) => {
    setConversations(prev => prev.map(c => c.id === id ? { ...c, unreadCount: 0 } : c))
    void convActions.markRead(id)
  }
  /**
   * Clear Chat — empty the thread FOR ME, keeping it in the list.
   *
   * The legacy UI map is reset alongside the real action because this page still
   * renders from `messagesStore` during the wire/UI coexistence window; the hook
   * is what makes it survive a reload, by moving the server-side floor and
   * purging IndexedDB. Without that half this was a repaint that the next
   * refresh undid.
   */
  const handleClearChatConv = (id: string, opts: { keepStarred: boolean } = { keepStarred: true }) => {
    setMessagesStore(prev => ({ ...prev, [id]: [] }))
    setConversations(prev => prev.map(c => c.id === id ? { ...c, lastMessage: '', unreadCount: 0 } : c))
    void convActions.clearChat(id, opts)
  }
  /**
   * Block the OTHER PARTY of a conversation (the menu item lives on the thread,
   * so it arrives as a conversation id, but a block is between people).
   *
   * The thread deliberately stays in the list with its history — blocking is not
   * deleting. What changes is that the server stops delivering their DMs, calls
   * and presence; the panel swaps the composer for an unblock prompt.
   */
  const handleBlockConv = (id: string) => {
    const conv = conversations.find(c => c.id === id)
    const peerId = conv && !conv.isGroup
      ? conv.participants?.find(p => p.id !== myUserId)?.id
      : undefined
    // Groups have no single peer to block — the menu shouldn't offer it there,
    // and doing nothing is safer than blocking an arbitrary member.
    if (!peerId) return
    void blockList.block(peerId, conv?.name ?? null)
  }
  /** Undo the above from the chat list, resolving the same peer id. */
  const handleUnblockConv = (id: string) => {
    const peerId = blockedPeerIdOf(conversations.find(c => c.id === id) ?? null)
    if (!peerId) return
    void blockList.unblock(peerId)
  }
  /**
   * Ring the other party of a DM from the contact-info panel.
   *
   * Peer id is their contactPresenceId, taken from the wire conversation when
   * it has loaded and from the local record otherwise — the same resolution
   * the conversation panel uses, because `startCall` keys a peer by that id and
   * nothing else will reach them.
   *
   * A group has no single peer to ring, so it is left alone rather than
   * calling an arbitrary member.
   */
  const startCallWithConversation = (conv: Conversation, mode: "audio" | "video") => {
    // `isBroadcast` as well as `isGroup`: a broadcast conversation carries no
    // `isGroup` flag, so it slipped past this and rang the first recipient.
    if (conv.isGroup || conv.isBroadcast) return
    const wire = useMessengerStore.getState().wireConversations.get(conv.id)
    const peerId =
      (wire?.participants ?? []).find((p) => p.userId !== myUserId)?.userId ??
      (conv.participants ?? []).find((p) => p.id !== myUserId)?.id
    if (!peerId) return
    setViewProfileConv(null)
    useCallsStore.getState().startCall(
      { id: peerId, name: conv.name, imageUrl: conv.imageUrl },
      mode,
      { conversationId: conv.id },
    )
  }

  const handleViewContact = (id: string) => {
    const conv = conversations.find(c => c.id === id) ?? null
    setViewProfileConv(conv)
  }

  /**
   * Server `StarredMessage` → the UI `Message` the Starred page renders.
   *
   * `id` is the real message id, so unstarring and jump-to-message both address
   * the actual message rather than a synthetic row.
   */
  const starredAsUiMessages = useMemo<Message[]>(
    () => stars.starred.map((s) => ({
      id: s.messageId,
      text: s.body ?? (s.attachmentKind ? ATTACHMENT_PREVIEW[s.attachmentKind] ?? "Attachment" : ""),
      time: s.createdAt ? new Date(s.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "",
      // The starred list is the one place messages from different days sit
      // next to each other, so it needs the day as well as the clock.
      createdAt: s.createdAt ?? undefined,
      isSentByMe: !!s.senderId && s.senderId === myUserId,
      status: "sent" as const,
      type: s.attachmentKind ? ATTACHMENT_UI_TYPE[s.attachmentKind] ?? "file" : "text",
      // The starred attachment itself, so the list can show the picture or the
      // file name rather than only the word "Photo" or "File".
      ...attachmentFields(s.attachments?.[0]),
      isStarred: true,
      senderName: s.senderName ?? (s.conversationName ?? undefined),
    })),
    [stars.starred, myUserId],
  )

  /** Open the starred message's thread, then scroll to it. */
  const handleJumpToStarred = (messageId: string) => {
    const row = stars.starred.find((s) => s.messageId === messageId)
    const conv = row?.conversationId
      ? conversations.find((c) => c.id === row.conversationId)
      : undefined
    if (!conv) {
      // Both misses used to return silently, which from the outside is
      // indistinguishable from the button being dead. A starred message whose
      // thread is no longer in the list — deleted, or not yet loaded — is worth
      // saying out loud.
      toast.error({ title: "Can't open that chat", description: "The conversation is no longer in your list." })
      return
    }
    setSettingsPage("main")
    setNavTab("chats")
    setSelectedConversation(conv)
    setJumpToMessageId(messageId)
  }

  /** Same jump, from the Contact Info drawer opened off the chat-list menu:
   *  that panel already has the conversation, so there is nothing to look up. */
  const handleJumpToFromProfile = (conv: Conversation, messageId: string) => {
    setViewProfileConv(null)
    setSettingsPage("main")
    setNavTab("chats")
    setSelectedConversation(conv)
    setJumpToMessageId(messageId)
  }

  /** The blocked other party of a DM, or undefined when nothing is blocked. */
  const blockedPeerIdOf = (conv: Conversation | null): string | undefined => {
    if (!conv || conv.isGroup) return undefined
    const peerId = conv.participants?.find(p => p.id !== myUserId)?.id
    return peerId && blockList.isBlocked(peerId) ? peerId : undefined
  }

  // Forward = re-send the message to each target over the real signal path
  // (live fan-out + Kafka storage), same as a normal send.
  //
  // Sourced from the WIRE message, not the UI one: the UI `Message` is flattened
  // to a single `attachmentUrl`/`attachmentName`/… set and carries no
  // `Attachment[]`, so rebuilding from it loses the attachment id/kind and
  // multi-attachment messages entirely. The wire row has the real array.
  const handleForwardMessage = (messages: Message[], targetIds: string[]) => {
    // Every selected message, in order. The hook sends them sequentially per
    // target so the batch lands in the same order it reads here.
    const sources = messages.map((message) => {
      const wire = findWireMessage(message.id, selectedConversation?.id)
      return wire
        ? { body: wire.body, attachments: wire.attachments, type: wire.type }
        // Not in the store (e.g. an old row never loaded as wire) — text-only
        // fallback, which is what this did for every message before.
        : { body: message.text ?? null }
    })
    void forwardMessage(sources, targetIds)
  }

  const handleUpdateConversation = (updates: Partial<Conversation>) => {
    setConversations(prev => prev.map(c => c.id === selectedConversation?.id ? { ...c, ...updates } : c))
    setSelectedConversation(prev => prev ? { ...prev, ...updates } : null)
  }

  const handleDeleteCurrentConversation = (opts: { keepStarred: boolean } = { keepStarred: true }) => {
    if (selectedConversation) {
      handleDeleteConv(selectedConversation.id, opts)
      setSelectedConversation(null)
    }
  }

  const handleExitCurrentGroup = () => {
    if (selectedConversation) void handleExitGroup(selectedConversation.id)
  }

  const handleMuteCurrentConversation = () => {
    if (selectedConversation) handleMuteConv(selectedConversation.id)
  }

  /**
   * New Chat and New Group are chats-tab screens, so they ask for the tab too.
   *
   * The right panel's empty state offers both from wherever you happen to be —
   * Broadcast, Calls, Settings — but the picker renders in the LEFT panel,
   * which is chosen by `navTab`. On Broadcast it opened behind the broadcast
   * list, and the effect that clears the picker on leaving Chats then closed
   * it again: the button did nothing, twice over.
   *
   * Set together, so that effect sees the tab already changed and leaves the
   * picker alone.
   */
  const handleNewChat = () => {
    setNavTab("chats")
    setSettingsPage("main")
    setShowNewChatDialog(true)
  }
  const handleNewGroup = () => {
    setNavTab("chats")
    setSettingsPage("main")
    setShowCreateGroupDialog(true)
  }

  const handleCreateGroup = async (groupData: { name: string; participants: string[]; imageUrl?: string; imageFile?: File }) => {
    try {
      // Upload the picked avatar first (if any) so the group carries a real
      // view URL — mirrors the attachment flow used for message media.
      let avatarUrl: string | null | undefined
      if (groupData.imageFile) {
        avatarUrl = await messengerApi.uploadAndResolveUrl(groupData.imageFile)
      }
      const { conversation: wireConv } = await messengerApi.createConversation({
        kind: "group",
        title: groupData.name,
        memberIds: groupData.participants,
        avatarUrl,
      })
      // Same store path the DM create uses: upsert into wire state (list +
      // channel subscription) and open it.
      useMessengerStore.getState().upsertWireConversation(wireConv)
      const ui: Conversation = {
        id: wireConv.id,
        name: wireConv.title ?? groupData.name,
        imageUrl: wireConv.avatarUrl ?? groupData.imageUrl,
        lastMessage: "",
        time: "",
        isOnline: false,
        unreadCount: 0,
        isSentByMe: false,
        messageStatus: "sent",
        isGroup: true,
        participants: (wireConv.participants ?? []).map(p => ({
          id: p.userId,
          name: p.name ?? `User ${p.userId.slice(0, 8)}`,
          imageUrl: p.avatarUrl ?? undefined,
          isAdmin: p.isAdmin,
        })),
      }
      setSelectedConversation(ui)
    } catch (err) {
      const msg = err instanceof MessengerApiError ? err.message : "createConversation failed"
      toast.error({ title: "Couldn't create group", description: msg })
       
      console.error("[messenger] createConversation(group):", msg)
    }
  }

  const handleSelectContact = async (contact: { id: string; name: string; imageUrl?: string }) => {
    setShowNewChatDialog(false)
    try {
      // POST is idempotent at the server (S2 canonical-pair unique index) —
      // returns the existing DM if one already exists for the same pair.
      const { conversation: wireConv } = await messengerApi.createConversation({
        kind: "dm",
        memberIds: [contact.id],
      })
      // Push into wire state so the new conversation appears in the list +
      // useMessengerSubscription joins its channel for live updates.
      useMessengerStore.getState().upsertWireConversation(wireConv)
      // Open it. Include a participants array containing the contact so the
      // conversation-header / chat-list-tile can resolve presence via
      // `useConversationOnline(c)` immediately — without it the hook has no
      // user-id to look up and the dot stays grey until the conv list refreshes.
      // `isOnline: false` here is just the placeholder the hook will override.
      const ui: Conversation = {
        id: wireConv.id,
        name: contact.name,
        imageUrl: contact.imageUrl,
        lastMessage: "",
        time: "",
        isOnline: false,
        unreadCount: 0,
        isSentByMe: false,
        messageStatus: "sent",
        participants: [
          { id: contact.id, name: contact.name, imageUrl: contact.imageUrl },
        ],
      }
      setSelectedConversation(ui)
    } catch (err) {
      const msg = err instanceof MessengerApiError ? err.message : "createConversation failed"
       
      console.error("[messenger] createConversation:", msg)
    }
  }

  const handleSelectBroadcast = (id: string) => {
    const broadcast = broadcasts.find(b => b.id === id)
    if (broadcast) {
      setSelectedBroadcast(broadcast)
      // A list's id IS a conversation id (the owner's sent-copy thread), so the
      // normal panel renders its history; `isBroadcast` routes SENDS through
      // the fan-out instead of a plain channel push.
      const broadcastConversation: Conversation = {
        id: broadcast.id,
        name: broadcast.name,
        lastMessage: broadcast.lastMessage,
        time: broadcast.time,
        isOnline: false,
        unreadCount: broadcast.unreadCount,
        isSentByMe: false,
        messageStatus: "delivered",
        isBroadcast: true,
        participants: broadcast.recipients,
      }
      setSelectedConversation(broadcastConversation)
    }
  }
  const handleCloseBroadcast = () => {
    setSelectedBroadcast(null)
    setSelectedConversation(null)
  }

  const handleCreateBroadcast = async (data: { name: string; participants: string[] }) => {
    const created = await createBroadcastList({ name: data.name, recipientIds: data.participants })
    if (!created) {
      toast.error({ title: "Couldn't create broadcast" })
      return
    }
    handleSelectBroadcastRow(created)
  }

  const handleSelectBroadcastRow = (broadcast: Broadcast) => {
    setSelectedBroadcast(broadcast)
    setSelectedConversation({
      id: broadcast.id,
      name: broadcast.name,
      lastMessage: broadcast.lastMessage,
      time: broadcast.time,
      isOnline: false,
      unreadCount: 0,
      isSentByMe: false,
      messageStatus: "delivered",
      isBroadcast: true,
      participants: broadcast.recipients,
    })
  }

  const handleRenameBroadcast = async (name: string) => {
    if (!selectedBroadcast) return
    try {
      await renameBroadcastList(selectedBroadcast.id, name)
      setSelectedBroadcast(prev => (prev ? { ...prev, name } : prev))
      setSelectedConversation(prev => (prev ? { ...prev, name } : prev))
    } catch (err) {
      toast.error({ title: "Couldn't rename broadcast", description: err instanceof Error ? err.message : undefined })
    }
  }

  const handleDeleteBroadcast = async () => {
    if (!selectedBroadcast) return
    try {
      await removeBroadcastList(selectedBroadcast.id)
      handleCloseBroadcast()
    } catch (err) {
      toast.error({ title: "Couldn't delete broadcast", description: err instanceof Error ? err.message : undefined })
    }
  }

  const handleRemoveBroadcastRecipient = async (userId: string) => {
    if (!selectedBroadcast) return
    try {
      await removeBroadcastRecipientFromList(selectedBroadcast.id, userId)
      setSelectedBroadcast(prev =>
        prev
          ? { ...prev, recipients: (prev.recipients ?? []).filter(r => r.id !== userId), recipientCount: Math.max(0, prev.recipientCount - 1) }
          : prev,
      )
    } catch (err) {
      toast.error({ title: "Couldn't remove recipient", description: err instanceof Error ? err.message : undefined })
    }
  }

  const handleOpenBlockedContacts = () => setSettingsPage("blocked-contacts")
  const handleOpenChatBackground  = () => setSettingsPage("chat-background")
  const handleOpenChatColor       = () => setSettingsPage("chat-color")
  const handleOpenStarred         = () => setSettingsPage("starred")
  const handleBackToSettings      = () => setSettingsPage("main")
  // Applying is not leaving.
  //
  // Both of these sent you back to the settings list, which made trying a
  // second background a three-click round trip and delivered the toast
  // confirming the first onto a screen that had already gone. `handleBackToSettings`
  // is still wired to the X in each page's header — that is the way out, and
  // it is the only one that should be.
  const handleSaveChatBackground = (bg: string) => {
    void chatPrefs.setChatBackground(bg || null)
  }
  const handleSaveChatColor = (color: string) => {
    void chatPrefs.setChatColor(color || null)
  }

  // Collect all starred messages across all conversations
  // (The Starred page now reads the server-backed list via `useStarredMessages`;
  // it previously scanned the legacy in-memory `messagesByConversation` store,
  // which only ever held whatever the session happened to have loaded.)

  const renderLeftContent = () => {
    switch (navTab) {
      case "chats":
        return showNewChatDialog ? (
          <NewChatPage
            contacts={chatPickerContacts}
            scope={pickerScope}
            resultScope={pickerResultScope}
            onScopeChange={setPickerScope}
            onQueryChange={setPickerQuery}
            loading={pickerLoading}
            hasMore={pickerHasMore}
            loadingMore={pickerLoadingMore}
            onLoadMore={loadMorePickerContacts}
            onClose={() => setShowNewChatDialog(false)}
            onSelectContact={handleSelectContact}
            onCreateGroup={() => { setShowNewChatDialog(false); setShowCreateGroupDialog(true) }}
            onCreateBroadcast={() => { setShowNewChatDialog(false); setNavTab("broadcast"); setShowCreateBroadcastDialog(true) }}
          />
        ) : (
          <ChatListPanel
            conversations={conversations}
            selectedId={selectedConversation?.id ?? null}
            onSelect={handleSelectConversation}
            onNewChat={handleNewChat}
            onArchive={handleArchiveConv}
            onPin={handlePinConv}
            onMute={handleMuteConv}
            onDelete={handleDeleteConv}
            onExitGroup={handleExitGroup}
            onFavorite={handleFavoriteConv}
            onMarkAsRead={handleMarkAsReadConv}
            onClearChat={handleClearChatConv}
            onBlock={handleBlockConv}
            isConversationBlocked={(conv) => !!blockedPeerIdOf(conv)}
            onUnblock={handleUnblockConv}
            onViewContact={handleViewContact}
          />
        )
      case "groups":
        return (
          <GroupsTab
            onSelectGroup={(g) => {
              const conv = conversations.find(c => c.name === g.name)
              if (conv) handleSelectConversation(conv)
            }}
          />
        )
      case "broadcast":
        return (
          <BroadcastTab
            broadcasts={broadcasts}
            isLoading={broadcastsLoading}
            error={broadcastsError}
            selectedId={selectedBroadcast?.id ?? null}
            onSelect={handleSelectBroadcast}
            onCreate={() => setShowCreateBroadcastDialog(true)}
          />
        )
      case "calls":
        return (
          <CallsTab
            onOpenChat={(contact) => {
              // Same path as picking someone from New Chat: the DM is created
              // or reused server-side, then opened. The tab has to change too,
              // or the conversation opens behind the calls list.
              setNavTab("chats")
              void handleSelectContact(contact)
            }}
          />
        )
      case "settings":
        if (settingsPage === "blocked-contacts") {
          return (
            <BlockedContactsPage
              blockedContacts={blockList.blocked.map(b => ({
                id: b.userId,
                name: b.name ?? "Unknown",
                imageUrl: b.avatarUrl ?? undefined,
              }))}
              onUnblock={(id) => void blockList.unblock(id)}
              onClose={handleBackToSettings}
            />
          )
        }
        if (settingsPage === "chat-background") {
          return <ChatBackgroundPage currentBackground={chatBackground} onClose={handleBackToSettings} onSave={handleSaveChatBackground} />
        }
        if (settingsPage === "chat-color") {
          return <ChatColorPage currentColor={chatColor} onClose={handleBackToSettings} onSave={handleSaveChatColor} />
        }
        if (settingsPage === "starred") {
          return (
            <StarredMessagesPage
              starredMessages={starredAsUiMessages}
              onClose={handleBackToSettings}
              onUnstar={(m) => void stars.toggleStar(m.id)}
              onJumpTo={handleJumpToStarred}
            />
          )
        }
        return (
          <SettingsTab
            profileName={profileName}
            profileImage={profileImage}
            onUpdateProfile={(name, image) => { setProfileName(name); if (image) setProfileImage(image) }}
            onOpenBlockedContacts={handleOpenBlockedContacts}
            onOpenChatBackground={handleOpenChatBackground}
            onOpenChatColor={handleOpenChatColor}
            onOpenStarred={handleOpenStarred}
          />
        )
    }
  }

  const hasConversation = !!selectedConversation
  const isChatsTab = navTab === "chats" || navTab === "groups"

  /**
   * Re-tint the presence UI to the chosen chat colour.
   *
   * These have to be COMPLETE colour values. This wrote a bare HSL triplet —
   * "210 80% 32%" — which is the shadcn convention, where stylesheets say
   * `hsl(var(--primary))` and supply the wrapper themselves. This project does
   * not: its tokens are raw colours (`--primary: #0C5A96`) and Tailwind maps
   * `--color-primary: var(--primary)` straight through.
   *
   * So `bg-primary` resolved to `background-color: 210 80% 32%`, which is not a
   * colour, so the browser dropped the declaration and the element rendered
   * with NO background — transparent, and therefore exactly the colour of the
   * page behind it. Every `bg-primary` inside this page vanished the moment a
   * chat colour was set: the selected scope chip, the ON state of every toggle,
   * and anything else tinted with it. `text-white` on nothing is white on
   * white, which is why the selected item looked absent rather than wrong.
   *
   * The hex needs no conversion — it is already what the token expects.
   */
  const themeStyle = useMemo((): CSSProperties | undefined => {
    if (!chatColor || !chatColor.startsWith("#") || chatColor.length < 7) return undefined
    return { "--primary": chatColor, "--primary-foreground": "#ffffff" } as CSSProperties
  }, [chatColor])

  const callContacts = useMemo(() =>
    conversations.filter(c => !c.isGroup).map(c => ({ id: c.id, name: c.name, imageUrl: c.imageUrl })),
    [conversations]
  )

  return (
    <div className="p-0 xl:p-5 w-full h-full min-h-0 min-w-0 overflow-hidden" style={themeStyle}>
      <ContactTermsGate
        open={contactGate.gated}
        busy={contactGate.status === "registering"}
        onAccept={contactGate.accept}
        onDismiss={contactGate.dismiss}
      />
      <div className="flex h-full w-full min-h-0 min-w-0 shadow-2xl bg-background overflow-hidden xl:rounded-xl">

        {/* ── Left panel ───────────────────────── */}
        <div
          className={`
            flex flex-col border-r border-border overflow-hidden shrink-0 min-h-0
            ${isChatsTab && hasConversation ? "hidden lg:flex" : "flex"}
            w-full lg:w-[340px] xl:w-[380px]
          `}
        >
          <div className="flex-1 min-h-0 overflow-hidden">
            {renderLeftContent()}
          </div>
          <ChatNavBar selected={navTab} onChange={(tab) => { setNavTab(tab); setSettingsPage("main") }} variant="bottom" />
        </div>

        {/* ── Right panel ─────── */}
        <div className="flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden">
          {selectedConversation ? (
            <ConversationPanel
              key={selectedConversation.id}
              conversation={selectedConversation}
              initialMessages={messagesStore[selectedConversation.id] ?? []}
              onClose={selectedBroadcast ? handleCloseBroadcast : handleCloseConversation}
              onForwardMessage={handleForwardMessage}
              blockedPeerId={blockedPeerIdOf(selectedConversation)}
              onUnblockPeer={(peerId) => void blockList.unblock(peerId)}
              onBlockPeer={() => handleBlockConv(selectedConversation.id)}
              starredIds={stars.starredIds}
              onToggleStar={(id) => void stars.toggleStar(id)}
              onDropStar={(id) => void stars.dropStar(id)}
              onOpenInvite={openInvite}
              jumpToMessageId={jumpToMessageId}
              onJumpHandled={() => setJumpToMessageId(null)}
              isMuted={!!conversations.find(c => c.id === selectedConversation.id)?.isMuted}
              onMuteChatToggle={handleMuteCurrentConversation}
              onDeleteConversation={handleDeleteCurrentConversation}
              onExitGroup={handleExitCurrentGroup}
              onClearConversation={(opts) => handleClearChatConv(selectedConversation.id, opts)}
              onUpdateConversation={handleUpdateConversation}
              chatBackground={
                // Per-chat override wins over the account default, exactly like
                // WhatsApp's per-thread wallpaper. `background` rides on the
                // conversation payload and is read from MY participant row, so
                // the other member's choice never leaks in here.
                useMessengerStore.getState().wireConversations.get(selectedConversation.id)?.background
                ?? chatBackground
              }
              sentBubbleColor={chatColor}
              broadcast={selectedBroadcast ?? undefined}
              onRenameBroadcast={handleRenameBroadcast}
              onDeleteBroadcast={handleDeleteBroadcast}
              onRemoveBroadcastRecipient={handleRemoveBroadcastRecipient}
              onBroadcastSent={refreshBroadcasts}
            />
          ) : (
            <EmptyState onNewChat={handleNewChat} onNewGroup={handleNewGroup} />
          )}
        </div>

      </div>

      {/* Incoming call overlay */}
      {incomingCall && !activeGlobalCall && (
        <IncomingCallOverlay
          callerName={incomingCall.name}
          callerImage={incomingCall.image}
          isVideo={incomingCall.isVideo}
          onAccept={() => {
            setActiveGlobalCall({ name: incomingCall.name, image: incomingCall.image, mode: incomingCall.isVideo ? "video" : "audio" })
            setIncomingCall(null)
          }}
          onDecline={() => setIncomingCall(null)}
        />
      )}

      {/* Global call screen (accepted incoming) */}
      {activeGlobalCall && (
        <CallScreen
          name={activeGlobalCall.name}
          imageUrl={activeGlobalCall.image}
          mode={activeGlobalCall.mode}
          contacts={callContacts}
          onClose={() => setActiveGlobalCall(null)}
        />
      )}

      {/* Create group dialog */}
      <CreateGroupDialog
        open={showCreateGroupDialog}
        onClose={() => setShowCreateGroupDialog(false)}
        onCreateGroup={handleCreateGroup}
      />

      {/* Create broadcast list — same picker, list semantics */}
      <CreateGroupDialog
        open={showCreateBroadcastDialog}
        onClose={() => setShowCreateBroadcastDialog(false)}
        onCreateGroup={handleCreateBroadcast}
        variant="broadcast"
      />

      {/* Invite link for a group the person is not yet in: see the effect above. */}
      <JoinGroupDialog
        open={!!joinInvite}
        groupName={joinInvite?.groupName ?? null}
        onJoin={handleJoinInviteConfirm}
        onCancel={handleJoinInviteCancel}
      />

      {/* View contact / group info — from chat list three-dot menu */}
      <Drawer open={!!viewProfileConv} onOpenChange={open => { if (!open) setViewProfileConv(null) }}>
        <DrawerContent direction="right" width="w-[420px]" className="p-0">
          {viewProfileConv && (
            <ProfileInfoPanel
              conversation={viewProfileConv}
              // Same rule as the conversation header: nothing to clear, no
              // option. System notices are not messages anyone sent.
              clearableCount={
                (messagesByConversation[viewProfileConv.id] ?? [])
                  .filter((m) => m.type !== "system").length
              }
              onClose={() => setViewProfileConv(null)}
              onJumpToMessage={(messageId) => handleJumpToFromProfile(viewProfileConv, messageId)}
              // These closed the panel and did nothing else — the same dead
              // buttons as the in-chat info panel, by a different route.
              onStartAudioCall={() => startCallWithConversation(viewProfileConv, "audio")}
              onStartVideoCall={() => startCallWithConversation(viewProfileConv, "video")}
              onClearChat={(opts) => handleClearChatConv(viewProfileConv.id, opts)}
              onDeleteChat={(opts) => { handleDeleteConv(viewProfileConv.id, opts); setViewProfileConv(null) }}
              onExitGroup={() => void handleExitGroup(viewProfileConv.id)}
              onUpdateProfile={(data) => {
                setConversations(prev => prev.map(c => c.id === viewProfileConv.id ? { ...c, ...data } : c))
                setViewProfileConv(prev => prev ? { ...prev, ...data } : null)
              }}
            />
          )}
        </DrawerContent>
      </Drawer>
    </div>
  )
}
